import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { User, Mail, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfileModal = ({
  showProfile,
  setShowProfile,
  user,
  onProfileUpdate
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form when modal opens
  useEffect(() => {
    if (showProfile && user) {
      setName(user.name || '');
      setEmail(user.email || '');
      setHasChanges(false);
    }
  }, [showProfile, user]);

  // Track changes
  useEffect(() => {
    if (user) {
      const nameChanged = name !== (user.name || '');
      const emailChanged = email.toLowerCase() !== user.email.toLowerCase();
      setHasChanges(nameChanged || emailChanged);
    }
  }, [name, email, user]);

  const handleSave = async () => {
    if (!hasChanges) return;

    const updates = {};
    if (name !== (user.name || '')) {
      updates.name = name.trim() || null;
    }
    if (email.toLowerCase() !== user.email.toLowerCase()) {
      updates.email = email.trim();
    }

    if (Object.keys(updates).length === 0) return;

    setSaving(true);
    try {
      const response = await axios.patch(`${API}/auth/profile`, updates);
      onProfileUpdate(response.data);
      toast.success('Profile updated successfully');
      setShowProfile(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={showProfile} onOpenChange={setShowProfile}>
      <DialogContent className="sm:max-w-md" data-testid="profile-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-emerald-400" />
            Profile Settings
          </DialogTitle>
          <DialogDescription>
            Update your profile information below
          </DialogDescription>
        </DialogHeader>

        {/* Form Content */}
        <div className="space-y-5 py-2">
          {/* Name Field */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              Display Name
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name"
              className="text-base"
              data-testid="profile-name-input"
            />
            <p className="text-xs text-muted-foreground">
              This is how you'll appear in shared conversations
            </p>
          </div>

          {/* Email Field */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              Email Address
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="text-base"
              data-testid="profile-email-input"
            />
            <p className="text-xs text-muted-foreground">
              Used for login and notifications
            </p>
          </div>

          {/* Account Info */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <p className="text-xs text-muted-foreground">Account created</p>
            <p className="text-sm">
              {user?.created_at 
                ? new Date(user.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })
                : 'Unknown'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setShowProfile(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="gap-2"
            data-testid="profile-save-btn"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileModal;
